import { useState } from 'react'
import { useRouter } from 'next/router'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm } from 'react-hook-form'

import api from 'lib/api'
import { useStore } from 'lib/store'
import { useToast } from 'hooks/useToast'

import { VacancyType } from 'types/vacancy'
import { Input, Radios } from 'components/Form'
import { Apply, applyVacancyInJobs } from '../utils'
import { Button, Modal, ModalAction } from 'components'
import {
  recommendationModalSchema,
  defaultValues
} from 'utils/form/recommendation-modal'
import { handleToAuth } from 'services/user'
import { HOTJAR_CLASSNAME } from 'utils/constants'

interface Props {
  id: number
  title: string
  type: VacancyType
  verifyRequiredFields: boolean
  pcdOnly: boolean
}

type ModalType =
  | 'review-curriculum'
  | 'internal-apply'
  | 'external-apply'
  | 'form-recommendation'
  | 'vacancy-pcd'
  | 'duplicate-acount'

export const CandidatureButton = ({
  verifyRequiredFields,
  ...vacancy
}: Props) => {
  const toast = useToast()
  const router = useRouter()
  const queryClient = useQueryClient()

  const { company, user, updateStoreUser } = useStore(store => store)
  const [modal, setModal] = useState<ModalType>(null)
  const [loadingApply, setLoadingApply] = useState<boolean>(false)
  const [applyStatus, setApplyStatus] = useState<ModalType>(null)

  const useFormMethods = useForm({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(recommendationModalSchema)
  })

  const isConfirmed = useFormMethods.watch('isConfirmed') === 'sim'

  const handleClickHeaderButton = async () => {
    if (!user) {
      handleToAuth()
      return
    } else if (applyStatus) {
      setModal(applyStatus)
      return
    }

    try {
      if (vacancy.type !== 'mista') {
        const route = `/applicant/check-employee/${vacancy.id}`
        // eslint-disable-next-line prettier/prettier
        const response = await api<{ isActiveEmployee: boolean }>(route, {}, 'applicants')

        if (!response.data.isActiveEmployee && vacancy.type === 'interna') {
          setModal('internal-apply')
          setApplyStatus('internal-apply')

          return
        } else if (
          response.data.isActiveEmployee &&
          vacancy.type === 'externa'
        ) {
          setModal('external-apply')
          setApplyStatus('external-apply')

          return
        }
      }

      if (verifyRequiredFields) {
        setLoadingApply(true)

        const route = '/home/vacancy/enabledtoapply'
        const params = { id: vacancy.id, email: user.email }
        const response = await api<string[]>(route, { params })

        setLoadingApply(false)

        if (response.data.length) {
          setModal('review-curriculum')
          setApplyStatus('review-curriculum')

          return
        }
      }

      setModal('form-recommendation')
    } catch (err) {
      console.error(err)
      console.log(err?.response?.message)
      toast.error({ title: 'Falha ao realizar a candidatura!' })
    }
  }

  const getApplyData = async (): Promise<Apply | null> => {
    const source = (router.query?.origem as string) || 'jobs'
    const metric = (router.query?.vacancies_collect_name as string) || ''

    const apply = {
      vacancy: { id: vacancy.id, title: vacancy.title, source },
      company: { name: company.name, slug: company.slug },
      metric,
      recommendations: null
    }

    if (isConfirmed) {
      const isValid = await useFormMethods.trigger()

      if (!isValid) {
        return null
      }

      const formData = useFormMethods.getValues()
      apply.recommendations = {
        name: formData.name,
        email: formData.email
      }
    }

    return apply
  }

  const handleReviewCurriculum = async () => {
    const apply = await getApplyData()
    applyVacancyInJobs(apply)
  }

  const handleApplyVacancy = async () => {
    const apply = await getApplyData()

    if (apply) {
      const data = {
        ...apply,
        vagaId: apply.vacancy.id,
        source: apply.vacancy.source
      }

      setLoadingApply(true)
      setModal(null)

      try {
        const route = '/applicant/apply-job'
        await api(route, { method: 'POST', data }, 'applicants')

        queryClient.setQueryData(['vacancy-applied', vacancy.id], true)
        toast.success({ title: 'Candidatura efetuada com sucesso!' })
        updateStoreUser({ appliedTalentBank: true })
      } catch (err) {
        console.error(err)

        const status = err?.response?.status
        const message = {
          title:
            (err?.response?.data?.message as string) ||
            'Falha ao concluir candidatura',
          description: ''
        }

        if (status === 400) {
          if (vacancy.pcdOnly) {
            setModal('vacancy-pcd')
            setLoadingApply(false)
            return
          }

          message.title = 'Revise seu currículo'
          message.description = 'Cadastro incompleto para esta vaga.'
        } else if (status === 409) {
          setModal('duplicate-acount')
        } else if (status === 401) {
          // profiler
        }

        toast.error(message)
      }

      setLoadingApply(false)
    }
  }

  const handleGoToSupport = () => {
    const url =
      'https://ajuda.solides.com.br/hc/pt-br/categories/4411290914189-Sou-candidato-a-'
    window.open(url, '_blank').focus()
  }

  return (
    <>
      <Modal
        size="800px"
        onClose={() => setModal(null)}
        className={`flex flex-col w-full ${HOTJAR_CLASSNAME}`}
        open={modal === 'form-recommendation'}
      >
        <FormProvider {...useFormMethods}>
          <form
            onSubmit={event => event.preventDefault()}
            className="s-form flex-1 flex flex-col gap-y-4"
          >
            <p className="text-sm font-semibold">Você possui indicação?</p>

            <Radios
              name="isConfirmed"
              options={[
                { text: 'Não', value: 'nao' },
                { text: 'Sim', value: 'sim' }
              ]}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Input
                name="name"
                label="Nome"
                placeholder="Nome do responsável pela indicação"
                required={isConfirmed}
                disabled={!isConfirmed}
              />
              <Input
                name="email"
                label="E-mail"
                placeholder="E-mail do responsável pela indicação"
                required={isConfirmed}
                disabled={!isConfirmed}
              />
            </div>

            <div className="mt-6 w-full grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                onClick={handleReviewCurriculum}
                className="py-3 bg-transparent text-primary !border !border-primary"
              >
                Revisar
              </Button>
              <Button className="py-3" onClick={handleApplyVacancy}>
                Efetuar candidatura
              </Button>
            </div>
          </form>
        </FormProvider>
      </Modal>

      <ModalAction
        icon="offline_bolt"
        open={modal === 'review-curriculum'}
        title={
          <>
            Você está a 1 passo
            <br />
            da sua candidatura
          </>
        }
        description="Para concluir sua candidatura você deverá revisar seu currículo e preencher os campos obrigatórios exigidos pela vaga. Deseja revisar seu currículo?"
        cancelButton={{ action: () => setModal(null) }}
        confirmButton={{ action: handleReviewCurriculum }}
      />

      <ModalAction
        icon="help"
        open={modal === 'duplicate-acount'}
        title="Não é possível se candidatar a vagas dessa empresa"
        description="O CPF fornecido já se candidatou a uma oportunidade com um outro e-mail para essa empresa e por isso não é possível continuar com a sua candidatura. Clique em cancelar e realize o log-in com a sua conta principal ou clique em Central de ajuda para entrar em contato com o nosso suporte."
        cancelButton={{ action: () => setModal(null) }}
        confirmButton={{ action: handleGoToSupport, label: 'Suporte' }}
      />

      <ModalAction
        icon="business_center"
        iconVariant="round"
        open={modal === 'internal-apply'}
        title="Candidatura Interna"
        description="Esta vaga foi planejada apenas para os colaboradores desta empresa. Caso você seja um colaborador, entre em contato com o seu RH."
        confirmButton={{ action: () => setModal(null), label: 'Ok, entendi!' }}
      />

      <ModalAction
        icon="business_center"
        iconVariant="round"
        open={modal === 'external-apply'}
        title="Candidatura Externa"
        description="Você não pode se candidatar a esta vaga, por ser colaborador desta empresa e a vaga trata-se de uma candidatura externa."
        confirmButton={{ action: () => setModal(null), label: 'Ok, entendi!' }}
      />

      <ModalAction
        icon="accessibility"
        iconVariant="round"
        open={modal === 'vacancy-pcd'}
        title="Não foi possível se candidatar"
        description="Essa é uma vaga destinada apenas a PCD (Pessoas com Deficiência). Portanto não é possível se candidatar"
        confirmButton={{ action: () => setModal(null), label: 'Ok, entendi!' }}
      />

      <Button
        colorCompany
        className="py-3 w-full sm:w-auto"
        onClick={handleClickHeaderButton}
        loading={loadingApply && 'Candidatando...'}
      >
        Quero me candidatar
      </Button>
    </>
  )
}
